/**
 * currency.js — Utilidad de formateo de moneda dinámica.
 * Lee currency_code / currency_symbol desde el contexto de la app.
 * Fallback: EUR / €
 *
 * Uso:
 *   import { formatPrice } from "@/utils/currency";
 *   formatPrice(12.5)          // "12,50 €"  (con config EUR)
 *   formatPrice(12.5, config)  // usa config.currency_code
 */

const DEFAULT_CURRENCY = {
    currency_code: "EUR",
    currency_symbol: "€",
    currency_position: "after", // "before" | "after"
    locale: "es-ES",
};

/**
 * Formatea un número como precio usando la configuración de moneda.
 * @param {number} amount
 * @param {object} currencyConfig  — objeto de restaurant_config (opcional)
 * @returns {string}
 */
export function formatPrice(amount, currencyConfig = null) {
    const cfg = currencyConfig || DEFAULT_CURRENCY;
    const code = cfg.currency_code || DEFAULT_CURRENCY.currency_code;
    const locale = cfg.locale || DEFAULT_CURRENCY.locale;

    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: code,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount || 0);
    } catch {
        // Fallback manual si el currency_code es desconocido
        const sym = cfg.currency_symbol || "€";
        const pos = cfg.currency_position || "after";
        const num = (amount || 0).toFixed(2).replace(".", ",");
        return pos === "before" ? `${sym}${num}` : `${num} ${sym}`;
    }
}

/**
 * Hook de conveniencia — devuelve la función formatPrice con config ya aplicada.
 * Usar cuando el componente ya tiene el objeto config.
 * @param {object} config
 * @returns {function}
 */
export function useCurrencyFormatter(config = null) {
    return (amount) => formatPrice(amount, config);
}

/** Símbolo de la moneda activa */
export function getCurrencySymbol(config = null) {
    const cfg = config || DEFAULT_CURRENCY;
    return cfg.currency_symbol || "€";
}
